import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import styled, { useTheme } from 'styled-components';
import { onAccent, statusColors } from '../../styles/consoleTheme';
import { IvrType, ttsModelLabels } from '../../types/ivr';
import {
  PlayIcon,
  MusicIcon,
  ContactIcon,
  PhoneIcon,
  ScheduleIcon,
  StopIcon,
  RepeatIcon
} from '../common/Icons';

export const ivrTypeLabels: Record<IvrType, string> = {
  START: '시작', ARS: 'ARS 안내·분기', PLAY: '음원 재생', RINGING_GROUP: '수신그룹',
  DIRECT: '직접연결', HANGUP: '통화 종료', REPEAT: '다시듣기', CONDITION_DAY: '요일별 분기',
  CONDITION_TIME: '시간별 분기', CONDITION_DATE: '날짜별 분기', CONDITION_HOLIDAY: '공휴일 제외', MENU_RETURN: '상위 메뉴 이동',
};
const getTypeColor = (type: IvrType, mode: 'light' | 'dark') => {
  const colors = statusColors[mode];
  if (type === 'START' || type === 'RINGING_GROUP') return colors.success;
  if (type.startsWith('CONDITION') || type === 'REPEAT' || type === 'MENU_RETURN') return colors.warning;
  if (type === 'HANGUP') return colors.danger;
  if (['ARS', 'PLAY', 'DIRECT'].includes(type)) return colors.info;
  return colors.neutral;
};

const NodeContainer = styled.div<{ $type: IvrType; $selected?: boolean }>`
  width: 260px; border-radius: 8px; position: relative;
  border: 1px solid ${({ $selected, $type, theme }) => $selected ? theme.color.point_y_100 : `${getTypeColor($type, theme.name)}80`};
  box-shadow: ${({ $selected, theme }) => $selected ? `0 0 0 1px ${theme.color.point_y_100}` : 'none'};
`;

const StyledHandle = styled(Handle)<{ $color?: string }>`
  width: 12px !important;
  height: 12px !important;
  background: ${({ $color, theme }) => $color || statusColors[theme.name].info} !important;
  border: 2px solid ${({ theme }) => theme.color.primary_100} !important;
  box-shadow: none;
  transition: width 0.16s ease, height 0.16s ease, box-shadow 0.16s ease !important;
  z-index: 100;

  &:hover {
    width: 16px !important;
    height: 16px !important;
    box-shadow: 0 0 0 5px ${({ $color, theme }) => ($color || statusColors[theme.name].info)}30;
  }

  &.react-flow__handle-connecting {
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.4);
  }

  &.react-flow__handle-valid {
    width: 16px !important;
    height: 16px !important;
    box-shadow: 0 0 0 6px ${({ $color, theme }) => ($color || statusColors[theme.name].info)}40;
  }

  &.react-flow__handle-top {
    top: 0;
  }
  &.react-flow__handle-bottom {
    bottom: 0;
  }
`;

const NodeInner = styled.div<{ $hasBranches?: boolean; $isStart?: boolean }>`
  background: ${({ $isStart, theme }) => $isStart ? statusColors[theme.name].success : theme.color.primary_100};
  border-radius: 7px;
  padding: ${({ $isStart }) => $isStart ? '12px 14px' : '14px'};
  ${({ $hasBranches }) => $hasBranches && 'padding-bottom: 46px;'}
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  z-index: 1;

  &::after {
    content: none;
  }
`;

const BranchLabels = styled.div`
  position: absolute;
  right: 0;
  bottom: 10px;
  left: 0;
  z-index: 2;
  height: 18px;
  pointer-events: none;

  span {
    position: absolute;
    top: 0;
    padding: 0;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    transform: translateX(-50%);
  }

  span:first-child {
    left: 30%;
    color: ${({ theme }) => statusColors[theme.name].success};
  }

  span:last-child {
    left: 70%;
    color: ${({ theme }) => statusColors[theme.name].danger};
  }
`;

const NodeHeader = styled.div<{ $isStart?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 9px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
  ${({ $isStart }) => $isStart && 'justify-content: center; gap: 8px; padding-bottom: 0; border-bottom: 0;'}
`;

const NodeTypeBadge = styled.span<{ $type: IvrType }>`
  font-size: 10px;
  padding: 3px 7px;
  border-radius: 6px;
  font-weight: 800;
  text-transform: uppercase;
  flex-shrink: 0;
  background: ${({ $type, theme }) => `${getTypeColor($type, theme.name)}18`};
  color: ${({ $type, theme }) => getTypeColor($type, theme.name)};
  border: 1px solid ${({ $type, theme }) => `${getTypeColor($type, theme.name)}40`};
  box-shadow: none;
`;

const NodeTitle = styled.button<{ $isStart?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  font-weight: 700;
  color: ${({ $isStart, theme }) => $isStart ? onAccent(theme) : theme.color.text_color_100};
  font-size: 13px;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${({ $isStart, theme }) => $isStart ? onAccent(theme) : theme.color.point_y_100};
    text-decoration: underline;
  }
  &:focus-visible { outline: 2px solid ${({ $isStart, theme }) => $isStart ? onAccent(theme) : theme.color.point_y_100}; outline-offset: 3px; }
`;

const NodeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.color.text_color_200};
  font-size: 12px;

  .clickable {
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    background: ${({ theme }) => theme.color.primary_50};
    border: 1px solid ${({ theme }) => theme.color.primary_300};
    color: inherit;
    font: inherit;
    text-align: left;
    transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;
    display: flex;
    align-items: flex-start;
    gap: 8px;

    & > span {
      padding-top: 1px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }

    &:hover {
      background: ${({ theme }) => theme.color.primary_100};
      border-color: ${({ theme }) => theme.color.point_y_100};
      color: ${({ theme }) => theme.color.text_color_100};
    }
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 10px;

    & > span {
      padding-top: 1px;
    }
  }
`;

const IconWrapper = styled.span<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color || 'inherit'};
  flex-shrink: 0;
`;

const IvrNode = ({ data, selected }: NodeProps) => {
  const theme = useTheme();
  const colors = statusColors[theme.name];
  const {
    name,
    ivr_type,
    audio_file_id,
    audio_filename,
    is_enterance,
    ringing_group_id,
    ringing_group_display_name,
    onEditContent,
    condition_days,
    condition_start_time,
    condition_end_time,
    condition_dates,
    direct_number,
    direct_name,
    custom_title,
  } = data as any;

  const dayLabels: { [key: string]: string } = {
    'MON': '월', 'TUE': '화', 'WED': '수', 'THU': '목', 'FRI': '금', 'SAT': '토', 'SUN': '일'
  };

  const getConditionText = () => {
    if (ivr_type === 'CONDITION_DAY') {
      if (!condition_days || condition_days.length === 0) return '요일 미설정';
      return condition_days.map((d: string) => dayLabels[d] || d).join(', ');
    }
    if (ivr_type === 'CONDITION_TIME') {
      if (!condition_start_time || !condition_end_time) return '시간 미설정';
      return `${condition_start_time} ~ ${condition_end_time}`;
    }
    if (ivr_type === 'CONDITION_DATE') {
      if (!condition_dates) return '날짜 미설정';
      const dates = condition_dates.split(',').map((d: string) => d.trim()).filter(Boolean);
      if (dates.length === 0) return '날짜 미설정';
      if (dates.length <= 3) {
        return dates.join('\n');
      }
      return `${dates.slice(0, 2).join('\n')}\n외 ${dates.length - 2} 건`;
    }
    if (ivr_type === 'CONDITION_HOLIDAY') {
      return '공휴일은 제외 처리';
    }
    return '조건 설정하기';
  };

  return (
    <NodeContainer data-testid="ivr-node" $type={ivr_type} $selected={selected}>
      {is_enterance !== 'Y' && (
        <StyledHandle
          type="target"
          position={Position.Top}
          $color={getTypeColor(ivr_type, theme.name)}
        />
      )}
      <NodeInner $hasBranches={ivr_type.startsWith('CONDITION')} $isStart={ivr_type === 'START'}>
        <NodeHeader $isStart={ivr_type === 'START'}>
          {ivr_type === 'START' && <IconWrapper $color={onAccent(theme)}><PlayIcon size={16} /></IconWrapper>}
          <NodeTitle
            $isStart={ivr_type === 'START'}
            type="button"
            className="nodrag nopan"
            aria-label="노드 제목 수정"
            title="사용자 지정 제목 수정"
            onClick={(event) => {
              event.stopPropagation();
              onEditContent?.('custom-title');
            }}
          >
            {custom_title?.trim() || name}
          </NodeTitle>
          {ivr_type !== 'START' && <NodeTypeBadge $type={ivr_type}>
            {ivrTypeLabels[ivr_type as IvrType] || ivr_type}
          </NodeTypeBadge>}
        </NodeHeader>
        {ivr_type !== 'START' && <NodeContent>
          {(ivr_type === 'ARS' || ivr_type === 'PLAY') && (
            <>
              <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('audio')}>
                <IconWrapper $color={colors.info}><MusicIcon /></IconWrapper>
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {data.audio_type === 'TTS'
                    ? (data.tts_text ? `TTS: ${data.tts_text}` : 'TTS 설정 안됨')
                    : (audio_filename || (audio_file_id ? `음원 ID: ${audio_file_id}` : '음원 선택 안됨'))
                  }
                </span>
              </button>
              {data.audio_type === 'TTS' && <small>TTS 모델: {data.tts_model == null ? ttsModelLabels.LOCAL : ttsModelLabels[data.tts_model as keyof typeof ttsModelLabels] || String(data.tts_model)}</small>}
            </>
          )}
          {ivr_type === 'REPEAT' && (
            <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('repeat')}>
              <IconWrapper $color={colors.warning}><RepeatIcon /></IconWrapper>
              <span>{`반복 횟수: ${data.repeat_count || 1}회`}</span>
            </button>
          )}
          {ivr_type === 'MENU_RETURN' && (
            <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('return-target')}>
              <IconWrapper $color={colors.warning}><RepeatIcon /></IconWrapper>
              <span>{data.return_target === 'START' ? '처음부터 다시 시작' : '이전 ARS 메뉴로 이동'}</span>
            </button>
          )}
          {ivr_type === 'RINGING_GROUP' && (
            <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('ringing-group')}>
              <IconWrapper $color={colors.success}><ContactIcon /></IconWrapper>
              <span>{ringing_group_display_name || (ringing_group_id ? `수신그룹 #${ringing_group_id}` : '수신그룹 선택 안됨')}</span>
            </button>
          )}
          {ivr_type === 'DIRECT' && (
            <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('direct')}>
              <IconWrapper $color={colors.info}><PhoneIcon /></IconWrapper>
              <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {direct_name || direct_number || '번호 선택 안됨'}
              </span>
            </button>
          )}
          {ivr_type.startsWith('CONDITION') && (
            <button type="button" className="clickable nodrag nopan" onClick={() => onEditContent?.('condition')}>
              <IconWrapper $color={colors.warning}><ScheduleIcon /></IconWrapper>
              <span style={{ whiteSpace: 'pre-wrap' }}>{getConditionText()}</span>
            </button>
          )}
          {ivr_type === 'HANGUP' && (
            <div className="info-row">
              <IconWrapper $color={colors.danger}><StopIcon /></IconWrapper>
              <span>종료</span>
            </div>
          )}
        </NodeContent>}
        {ivr_type.startsWith('CONDITION') && (
          <BranchLabels data-testid="condition-branch-labels" aria-label="조건 분기 결과">
            <span>참</span>
            <span>그 외</span>
          </BranchLabels>
        )}
      </NodeInner>
      {(ivr_type === 'START' || ivr_type === 'PLAY') && (
        <StyledHandle
          type="source"
          position={Position.Bottom}
          $color={ivr_type === 'START' ? colors.success : colors.info}
        />
      )}
      {ivr_type === 'ARS' && (
        <>
          {/* Default handle for dragging and old data */}
          <StyledHandle
            type="source"
            position={Position.Bottom}
            $color={colors.info}
            style={{ left: '50%' }}
          />
          {/* Specific keypad handles for sourceHandle identification */}
          {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'].map((key) => (
            <StyledHandle
              key={key}
              id={key}
              type="source"
              position={Position.Bottom}
              $color={colors.info}
              style={{ left: '50%', opacity: 0, pointerEvents: 'none' }}
            />
          ))}
        </>
      )}
      {ivr_type.startsWith('CONDITION') && (
        <>
          <StyledHandle
            type="source"
            position={Position.Bottom}
            id="true"
            $color={colors.success}
            style={{ left: '30%' }}
          />
          <StyledHandle
            type="source"
            position={Position.Bottom}
            id="false"
            $color={colors.danger}
            style={{ left: '70%' }}
          />
        </>
      )}
    </NodeContainer>
  );
};

export default memo(IvrNode);
